import { useEffect, useMemo, useRef, useState } from "react";
import type { Bbox, PageDim } from "../api";

// pdfjs via ES-module worker (Vite handelt ?url af als string import)
import * as pdfjs from "pdfjs-dist";
// @ts-expect-error vite-asset-url module declaration niet bijgeleverd door pdfjs
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export type OverlayKind = "veld" | "tabel" | "mist";

export interface Overlay {
  id: string;         // unieke id voor hover-sync (bv "veld:factuurnummer")
  kind: OverlayKind;
  page: number;
  bbox: Bbox;         // TurboOCR coords in page-px
  label: string;      // weergave bij hover (bv "factuurnummer = 2026659")
}

interface Props {
  pdfUrl: string;
  pageDims: PageDim[];   // TurboOCR page-afmetingen (px) om overlay te schalen
  overlays: Overlay[];
  activeId: string | null;
  onHover: (id: string | null) => void;
  onOverlayClick?: (id: string, viewportTop: number) => void;
  renderScale?: number;  // CSS-px per TurboOCR-px (default 1.0 — TurboOCR levert al bij ~100dpi)
}

interface RenderedPage {
  pageNumber: number;
  canvasWidth: number;   // actual rendered canvas pixels
  canvasHeight: number;
  cssWidth: number;      // CSS display pixels
  cssHeight: number;
  turboWidth: number;    // TurboOCR page-px for coord-scaling
  turboHeight: number;
}

const COLORS: Record<OverlayKind, { border: string; bg: string; bgActive: string }> = {
  veld:  { border: "#10b981", bg: "rgba(16, 185, 129, 0.10)", bgActive: "rgba(16, 185, 129, 0.35)" },
  tabel: { border: "#3b82f6", bg: "rgba(59, 130, 246, 0.08)", bgActive: "rgba(59, 130, 246, 0.28)" },
  mist:  { border: "#ef4444", bg: "rgba(239, 68, 68, 0.10)", bgActive: "rgba(239, 68, 68, 0.30)" },
};

/**
 * Rendert alle pagina's van een PDF stacked en legt per pagina een overlay-laag
 * met bbox-rechthoeken. Overlay-coords worden geschaald van TurboOCR page-px → canvas-css-px.
 */
export function PdfViewer({ pdfUrl, pageDims, overlays, activeId, onHover, onOverlayClick, renderScale = 1.5 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPages([]);

    (async () => {
      try {
        const task = pdfjs.getDocument({ url: pdfUrl, withCredentials: true });
        const doc = await task.promise;
        const rendered: RenderedPage[] = [];
        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n);
          const viewport = page.getViewport({ scale: renderScale });
          const canvas = canvasRefs.current[n - 1];
          if (!canvas) continue;
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          await page.render({ canvasContext: ctx, viewport }).promise;
          const turbo = pageDims.find((d) => d.page === n - 1);
          rendered.push({
            pageNumber: n,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            cssWidth: viewport.width,
            cssHeight: viewport.height,
            turboWidth: turbo?.width ?? viewport.width / renderScale,
            turboHeight: turbo?.height ?? viewport.height / renderScale,
          });
          if (cancelled) return;
          setPages([...rendered]);
        }
        if (!cancelled) setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pdfUrl, pageDims, renderScale]);

  // Scroll naar de bbox van de actieve overlay — ALLEEN de pdf-viewer, niet de
  // hele pagina (scrollIntoView zou alle scrollbare ancestors raken, inclusief
  // body, en dan springt de layout waardoor click-sync met de sidebar misgaat).
  useEffect(() => {
    if (!activeId || !containerRef.current) return;
    const container = containerRef.current;
    const el = container.querySelector(`[data-overlay-id="${activeId}"]`) as HTMLElement | null;
    if (!el) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const alreadyVisible = elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;
    if (alreadyVisible) return;
    const target =
      container.scrollTop +
      (elRect.top - containerRect.top) -
      containerRect.height / 2 +
      elRect.height / 2;
    container.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
  }, [activeId]);

  // Groepeer overlays per pagina voor snelle lookup
  const overlaysByPage = useMemo(() => {
    const m = new Map<number, Overlay[]>();
    for (const o of overlays) {
      const list = m.get(o.page) ?? [];
      list.push(o);
      m.set(o.page, list);
    }
    return m;
  }, [overlays]);

  const numPages = Math.max(pageDims.length, pages.length, 1);

  return (
    <div ref={containerRef} className="pdf-viewer h-full overflow-y-auto bg-gray-100 rounded border">
      {error && (
        <div className="p-4 text-sm text-red-800 bg-red-50 border-b border-red-200">
          PDF-render fout: {error}
        </div>
      )}
      {loading && !pages.length && (
        <div className="p-6 text-center text-sm text-gray-500">PDF laden…</div>
      )}
      <div className="p-4 space-y-6">
        {Array.from({ length: numPages }).map((_, i) => {
          const pageInfo = pages.find((p) => p.pageNumber === i + 1);
          const pageOverlays = overlaysByPage.get(i) ?? [];
          const scaleX = pageInfo ? pageInfo.cssWidth / pageInfo.turboWidth : 1;
          const scaleY = pageInfo ? pageInfo.cssHeight / pageInfo.turboHeight : 1;
          return (
            <div
              key={i}
              className="relative mx-auto bg-white shadow"
              style={{
                width: pageInfo ? pageInfo.cssWidth : undefined,
                height: pageInfo ? pageInfo.cssHeight : undefined,
              }}
            >
              <canvas
                ref={(el) => {
                  canvasRefs.current[i] = el;
                }}
                className="block"
              />
              {pageInfo && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: pageInfo.cssWidth, height: pageInfo.cssHeight }}
                >
                  {pageOverlays.map((o) => (
                    <OverlayRect
                      key={o.id}
                      overlay={o}
                      scaleX={scaleX}
                      scaleY={scaleY}
                      active={o.id === activeId}
                      onHover={onHover}
                      onClick={onOverlayClick}
                    />
                  ))}
                </div>
              )}
              {pageInfo && (
                <div className="absolute top-1 right-2 text-[10px] font-mono text-gray-400">
                  pagina {i + 1}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverlayRect({
  overlay,
  scaleX,
  scaleY,
  active,
  onHover,
  onClick,
}: {
  overlay: Overlay;
  scaleX: number;
  scaleY: number;
  active: boolean;
  onHover: (id: string | null) => void;
  onClick?: (id: string, viewportTop: number) => void;
}) {
  const bbox = overlay.bbox;
  if (!bbox || bbox.length < 4) return null;
  const xs = bbox.map((p) => p[0]);
  const ys = bbox.map((p) => p[1]);
  const minX = Math.min(...xs) * scaleX;
  const minY = Math.min(...ys) * scaleY;
  const maxX = Math.max(...xs) * scaleX;
  const maxY = Math.max(...ys) * scaleY;
  const w = Math.max(2, maxX - minX);
  const h = Math.max(2, maxY - minY);
  const c = COLORS[overlay.kind];

  return (
    <div
      data-overlay-id={overlay.id}
      className="absolute pointer-events-auto cursor-pointer transition-colors"
      style={{
        left: minX,
        top: minY,
        width: w,
        height: h,
        border: `2px solid ${c.border}`,
        backgroundColor: active ? c.bgActive : c.bg,
        borderRadius: 3,
        boxShadow: active ? `0 0 0 2px ${c.border}66` : undefined,
      }}
      title={overlay.label}
      onMouseEnter={() => onHover(overlay.id)}
      onMouseLeave={() => onHover(null)}
      onClick={(e) => onClick?.(overlay.id, e.currentTarget.getBoundingClientRect().top)}
    />
  );
}
